import api from "./api";

/**
 * Get workspace list with filters and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.page_size - Items per page (default: 20)
 * @param {Array<string>} params.order_by - Fields to order by
 * @param {Array<string>} params.order_direction - Order direction (ASC/DESC)
 * @param {Object} params.search - Search object with keyword
 * @param {Object} params.filters - Filter object
 * @returns {Promise<Object>} API response with body and meta
 */
export const getWorkspaceList = async (params = {}) => {
  const { 
    page = 1, 
    page_size = 20, 
    search, 
    order_by = ["created_at"], 
    order_direction = ["desc"] 
  } = params;

  // Map order_by fields to backend format
  // Backend expects "name", "created_at", or "updated_at" for sorting
  const orderByMap = {
    created_at: "created_at",
    name: "name", // Backend field for tenant name
    updated_at: "updated_at", // Backend field for last updated
    // TODO: Uncomment when backend supports sorting by contact_name
    // contact_name: "contact_name", // Backend field for contact name
  };

  // Normalize order_by to array and map
  const normalizedOrderBy = Array.isArray(order_by) ? order_by : [order_by];
  const mappedOrderBy = normalizedOrderBy.map(field => orderByMap[field] || field);

  // Normalize order_direction to array (keep lowercase as backend expects)
  const normalizedOrderDirection = Array.isArray(order_direction) 
    ? order_direction
    : [order_direction];

  const body = {
    page,
    page_size,
    order_by: mappedOrderBy,
    order_direction: normalizedOrderDirection,
  };

  if (search && search.keyword) {
    body.search = {
      keyword: search.keyword,
    };
  }

  const response = await api.post("/v1/workspace/list", body);
  return response.data;
};

/**
 * Get workspace by ID/UUID
 * @param {string} workspaceId - Workspace ID/UUID (tenant_uuid)
 * @returns {Promise<Object>} API response with workspace data
 */
export const getWorkspaceById = async (workspaceId) => {
  if (!workspaceId) {
    throw new Error("workspaceId is required");
  }

  const response = await api.get(`/v1/workspace/${workspaceId}`);
  return response.data;
};

/**
 * Update workspace by tenant_uuid
 * @param {Object} workspaceData - Updated workspace data
 * @param {string} workspaceData.tenant_uuid - Tenant UUID (required)
 * @param {string} workspaceData.tenant_name - Workspace name (required)
 * @param {string} workspaceData.contact_email - Contact email (required)
 * @param {string} workspaceData.address - Address (optional)
 * @returns {Promise<Object>} API response with updated workspace
 */
export const updateWorkspace = async (workspaceData) => {
  if (!workspaceData.tenant_uuid) {
    throw new Error("tenant_uuid is required");
  }
  if (!workspaceData.tenant_name) {
    throw new Error("tenant_name is required");
  }
  if (!workspaceData.contact_email) {
    throw new Error("contact_email is required");
  }

  const body = {
    workspace_uuid: workspaceData.tenant_uuid,
    workspace_name: workspaceData.tenant_name,
    contact_email: workspaceData.contact_email,
    address: workspaceData.address || '',
  };

  const response = await api.post("/v1/workspace/update", body);
  return response.data;
};

/**
 * Create a new workspace
 * @param {Object} workspaceData - Workspace data
 * @param {string} workspaceData.name - Workspace name
 * @param {Array<string>} workspaceData.slug - Workspace slug array
 * @param {string} workspaceData.contact_email - Contact email
 * @param {string} workspaceData.contact_name - Contact name
 * @param {string} workspaceData.phone_number - Phone number
 * @param {string} workspaceData.billing_plan - Billing plan
 * @param {number} workspaceData.organization_id - Organization ID
 * @param {string} workspaceData.contact_address - Contact address (optional)
 * @param {File} workspaceData.logoFile - Logo file (optional)
 * @returns {Promise<Object>} API response with created workspace
 */
export const createWorkspace = async (workspaceData) => {
  const { logoFile, ...otherData } = workspaceData;
  
  // Normalize slug: always convert to string (backend expects string, not array)
  const normalizeSlug = (slug) => {
    if (Array.isArray(slug)) {
      return slug.length > 0 ? String(slug[0]) : '';
    }
    return slug ? String(slug) : '';
  };
  
  const slugString = normalizeSlug(otherData.slug);
  
  // Always use JSON format
  const body = {
    name: otherData.name,
    slug: slugString,
    contact_email: otherData.contact_email || '',
    contact_name: otherData.contact_name || '',
    phone_number: otherData.phone_number || '',
    billing_plan: otherData.billing_plan || '',
    organization_id: otherData.organization_id || 1,
    contact_address: otherData.contact_address || '',
  };

  // If there's a logo file, add photo metadata only (no file content)
  if (logoFile) {
    // Generate unique identifier for logical_name
    const uniqueId = crypto.randomUUID();
    body.logical_name = uniqueId;
    body.filename = logoFile.name;
    body.file_type = logoFile.type;
    body.path = `tenants/profile/${logoFile.name}`;
  }

  const response = await api.post("/v1/workspace/create", body);
  return response.data;
};

/**
 * Activate a workspace by tenant_uuid
 * @param {string} tenantUuid - Tenant UUID
 * @returns {Promise<Object>} API response
 */
export const activateWorkspace = async (tenantUuid) => {
  if (!tenantUuid) {
    throw new Error("tenant_uuid is required");
  }

  const body = {
    workspace_uuid: tenantUuid,
  };

  const response = await api.post("/v1/workspace/activate", body);
  return response.data;
};

/**
 * Deactivate a workspace by tenant_uuid
 * @param {string} tenantUuid - Tenant UUID
 * @returns {Promise<Object>} API response
 */
export const deactivateWorkspace = async (tenantUuid) => {
  if (!tenantUuid) {
    throw new Error("tenant_uuid is required");
  }

  const body = {
    workspace_uuid: tenantUuid,
  };

  const response = await api.post("/v1/workspace/deactivate", body);
  return response.data;
};

/**
 * Upload tenant photo
 * @param {string} tenantUuid - Tenant UUID
 * @param {File} file - File to upload
 * @returns {Promise<Object>} API response with file metadata
 */
export const uploadTenantPhoto = async (tenantUuid, file) => {
  if (!tenantUuid) {
    throw new Error("tenantUuid is required");
  }
  if (!file) {
    throw new Error("file is required");
  }

  // Generate unique identifier for logical_name (same as create)
  const uniqueId = crypto.randomUUID();
  const photoData = {
    workspace_uuid: tenantUuid,
    logical_name: uniqueId,
    filename: file.name,
    path: `tenants/${tenantUuid}/profile/${file.name}`,
    type: file.type,
    tags: ["tenant_profile"],
  };

  const response = await api.post(`/v1/workspace/photo`, photoData);
  return response.data;
};

/**
 * Get audit list with filters and pagination
 * POST /v1/audit/list
 * 
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1, must be >= 1)
 * @param {number} params.page_size - Items per page (default: 20, must be between 1 and 100)
 * @param {string} params.order_by - Fields to order by (default: "created_at")
 *   Allowed: "created_at", "user_full_name", "action", "section"
 * @param {string} params.order_direction - Order direction (default: "DESC")
 *   Allowed: "ASC", "DESC"
 * @param {Object} params.search - Search object
 * @param {string} params.search.keyword - Search term applied to description (case-insensitive contains)
 * @param {Object} params.filters - Filter object
 * @param {string} params.filters.action - Exact match on action (e.g., "Modified", "Added", "Updated", "Login", "Deleted")
 * @param {string} params.filters.user_uuid - Filter by user UUID (must be valid UUID v4)
 * @param {string} params.filters.section_uuid - Filter by section/entity UUID (must be valid UUID v4)
 * @param {string} params.filters.modified_from - Lower bound for created_at (ISO 8601 datetime)
 * @param {string} params.filters.modified_to - Upper bound for created_at (ISO 8601 datetime)
 * @returns {Promise<Object>} API response { status_code, msg_code, body, meta, request_id }
 */
export const getAuditList = async (params = {}) => {
  const { 
    page = 1, 
    page_size = 20, 
    order_by = "created_at", 
    order_direction = "DESC",
    search, 
    filters 
  } = params;

  // Normalize order_by to string (backend expects string, not array)
  const normalizedOrderBy = Array.isArray(order_by) ? order_by[0] : order_by;

  // Validate order_by
  const validOrderBy = ["created_at", "user_full_name", "action", "section"];
  if (!validOrderBy.includes(normalizedOrderBy)) {
    throw new Error(`Invalid order_by: ${normalizedOrderBy}. Allowed values: ${validOrderBy.join(", ")}`);
  }

  // Normalize order_direction to string (backend expects uppercase string)
  const normalizedOrderDirection = Array.isArray(order_direction) 
    ? order_direction[0]?.toUpperCase() || "DESC"
    : order_direction.toUpperCase();

  // Validate order_direction
  if (!["ASC", "DESC"].includes(normalizedOrderDirection)) {
    throw new Error(`Invalid order_direction: ${normalizedOrderDirection}. Allowed values: ASC, DESC`);
  }

  const body = {
    page,
    page_size,
    order_by: normalizedOrderBy,
    order_direction: normalizedOrderDirection,
  };

  // Add search if provided
  if (search && search.keyword) {
    body.search = {
      keyword: search.keyword,
    };
  }

  // Add filters if provided
  if (filters && Object.keys(filters).length > 0) {
    body.filters = filters;
  }

  const response = await api.post("/v1/audit/list", body);
  return response.data;
};

/**
 * Delete workspace by ID/UUID
 * @param {string} workspaceId - Workspace ID/UUID
 * @returns {Promise<Object>} API response
 */
export const deleteWorkspace = async (workspaceId) => {
  // TODO: Implement API call
  // if (!workspaceId) {
  //   throw new Error("workspaceId is required");
  // }
  // const response = await api.delete(`/v1/workspace/${workspaceId}`);
  // return response.data;
};

